// src/styles/theme.js

// ── Default color palette ────────────────────────────────
const DEFAULT_COLORS = {
    cream: '#f5f0eb',
    creamDark: '#ede6de',
    terracotta: '#c4796b',
    terracottaDark: '#a8604f',
    terracottaLight: '#d4938a',
    charcoal: '#2d2926',
    warmGray: '#8a837d',
    white: '#ffffff',
    success: '#6b9b7a',
    danger: '#c46b6b',
    link: '',
    linkHover: '',
    cardBg: '',
    navCardBg: '',
    tileBg: '',
    stat: '',
    filterPillBg: '',
    filterPillText: '',
    filterPillActiveBg: '',
    filterPillActiveText: '',
};

// ── Derived / computed values ─────────────────────────────
function buildTheme(colors, fonts) {
    const c = { ...DEFAULT_COLORS, ...colors };

    // Derive overlay and gradient colors from the base palette
    c.overlay = hexToRgba(c.charcoal, 0.6);
    c.charcoalLight = blendHex(c.charcoal, c.warmGray, 0.35);

    const f = {
        serif: (fonts && fonts.serif) || "'Libre Baskerville', 'Georgia', serif",
        sans: (fonts && fonts.sans) || "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    };

    return {
        colors: c,
        fonts: f,
        radius: { sm: '8px', md: '14px', lg: '20px', xl: '24px', pill: '50px' },
        shadows: {
            sm: '0 2px 12px rgba(0,0,0,0.04)',
            md: '0 4px 20px rgba(0,0,0,0.06)',
            lg: '0 12px 32px rgba(0,0,0,0.1)',
            xl: '0 20px 60px rgba(0,0,0,0.15)',
            primary: `0 8px 25px ${hexToRgba(c.terracotta, 0.35)}`,
        },
        transitions: {
            default: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            fast: 'all 0.15s ease',
        },
    };
}

// ── Apply theme as CSS custom properties ──────────────────
function applyThemeToDOM(colors, fonts) {
    const c = { ...DEFAULT_COLORS, ...colors };
    const root = document.documentElement;
    root.style.setProperty('--mom-cream', c.cream);
    root.style.setProperty('--mom-cream-dark', c.creamDark);
    root.style.setProperty('--mom-primary', c.terracotta);
    root.style.setProperty('--mom-primary-dark', c.terracottaDark);
    root.style.setProperty('--mom-primary-light', c.terracottaLight);
    root.style.setProperty('--mom-charcoal', c.charcoal);
    root.style.setProperty('--mom-charcoal-light', blendHex(c.charcoal, c.warmGray, 0.35));
    root.style.setProperty('--mom-warm-gray', c.warmGray);
    root.style.setProperty('--mom-white', c.white);
    root.style.setProperty('--mom-success', c.success);
    root.style.setProperty('--mom-danger', c.danger);
    root.style.setProperty('--mom-overlay', hexToRgba(c.charcoal, 0.6));
    root.style.setProperty('--mom-primary-shadow', `0 8px 25px ${hexToRgba(c.terracotta, 0.35)}`);
    // Link colors: fall back to primary if not set
    root.style.setProperty('--mom-link', c.link || c.terracotta);
    root.style.setProperty('--mom-link-hover', c.linkHover || c.terracottaDark);
    // Card backgrounds: fall back to white/cream if not set
    root.style.setProperty('--mom-card-bg', c.cardBg || c.white);
    root.style.setProperty('--mom-nav-card-bg', c.navCardBg || c.cream);
    // Tile background: fall back to white if not set
    root.style.setProperty('--mom-tile-bg', c.tileBg || c.white);
    // Stat color: fall back to primary if not set
    root.style.setProperty('--mom-stat', c.stat || c.terracotta);
    // Filter pill colors: fall back to cream/charcoal (unselected) and primary/white (selected)
    root.style.setProperty('--mom-filter-pill-bg', c.filterPillBg || c.cream);
    root.style.setProperty('--mom-filter-pill-text', c.filterPillText || c.charcoal);
    root.style.setProperty('--mom-filter-pill-active-bg', c.filterPillActiveBg || c.terracotta);
    root.style.setProperty('--mom-filter-pill-active-text', c.filterPillActiveText || c.white);

    // Apply fonts if provided
    if (fonts) {
        if (fonts.serif) root.style.setProperty('--mom-font-serif', fonts.serif);
        if (fonts.sans) {
            root.style.setProperty('--mom-font-sans', fonts.sans);
            document.body.style.fontFamily = fonts.sans;
        }
    }
}

// ── Curated font options ──────────────────────────────────
const HEADING_FONT_OPTIONS = [
    { key: 'libre-baskerville', label: 'Libre Baskerville', family: "'Libre Baskerville', Georgia, serif", google: 'Libre+Baskerville:ital,wght@0,400;0,700;1,400' },
    { key: 'tan-ashford', label: 'TAN Ashford', family: "'TAN Ashford', Georgia, serif", selfHosted: true, files: [{ weight: 400, file: '/fonts/TAN-ASHFORD.woff2' }, { weight: 700, file: '/fonts/TAN-ASHFORD-Bold.woff2' }] },
    { key: 'garuda', label: 'Garuda', family: "'Garuda', Georgia, serif", selfHosted: true, files: [{ weight: 400, file: '/fonts/GARUDA.woff2' }, { weight: 700, file: '/fonts/GARUDA-Bold.woff2' }] },
    { key: 'playfair-display', label: 'Playfair Display', family: "'Playfair Display', Georgia, serif", google: 'Playfair+Display:ital,wght@0,400;0,700;1,400' },
    { key: 'lora', label: 'Lora', family: "'Lora', Georgia, serif", google: 'Lora:ital,wght@0,400;0,700;1,400' },
    { key: 'cormorant-garamond', label: 'Cormorant Garamond', family: "'Cormorant Garamond', Georgia, serif", google: 'Cormorant+Garamond:ital,wght@0,400;0,600;1,400' },
    { key: 'dm-serif-display', label: 'DM Serif Display', family: "'DM Serif Display', Georgia, serif", google: 'DM+Serif+Display:ital@0;1' },
    { key: 'eb-garamond', label: 'EB Garamond', family: "'EB Garamond', Georgia, serif", google: 'EB+Garamond:ital,wght@0,400;0,700;1,400' },
    { key: 'source-serif-4', label: 'Source Serif 4', family: "'Source Serif 4', Georgia, serif", google: 'Source+Serif+4:ital,wght@0,400;0,700;1,400' },
    { key: 'crimson-pro', label: 'Crimson Pro', family: "'Crimson Pro', Georgia, serif", google: 'Crimson+Pro:ital,wght@0,400;0,700;1,400' },
];

const BODY_FONT_OPTIONS = [
    { key: 'dm-sans', label: 'DM Sans', family: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", google: 'DM+Sans:wght@400;500;600;700' },
    { key: 'libre-franklin', label: 'Libre Franklin', family: "'Libre Franklin', -apple-system, sans-serif", google: 'Libre+Franklin:wght@400;500;600;700' },
    { key: 'inter', label: 'Inter', family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", google: 'Inter:wght@400;500;600;700' },
    { key: 'nunito', label: 'Nunito', family: "'Nunito', -apple-system, sans-serif", google: 'Nunito:wght@400;500;600;700' },
    { key: 'open-sans', label: 'Open Sans', family: "'Open Sans', -apple-system, sans-serif", google: 'Open+Sans:wght@400;500;600;700' },
    { key: 'lato', label: 'Lato', family: "'Lato', -apple-system, sans-serif", google: 'Lato:wght@400;700' },
    { key: 'raleway', label: 'Raleway', family: "'Raleway', -apple-system, sans-serif", google: 'Raleway:wght@400;500;600;700' },
    { key: 'work-sans', label: 'Work Sans', family: "'Work Sans', -apple-system, sans-serif", google: 'Work+Sans:wght@400;500;600;700' },
    { key: 'outfit', label: 'Outfit', family: "'Outfit', -apple-system, sans-serif", google: 'Outfit:wght@400;500;600;700' },
];

// ── Load Google Fonts dynamically ─────────────────────────
function loadGoogleFonts(headingKey, bodyKey) {
    const allOptions = [...HEADING_FONT_OPTIONS, ...BODY_FONT_OPTIONS];
    const selected = [headingKey, bodyKey].map((k) => allOptions.find((f) => f.key === k)).filter(Boolean);

    // Load Google Fonts
    const googleFamilies = selected.filter((f) => f.google).map((f) => f.google);
    if (googleFamilies.length > 0) {
        const existing = document.getElementById('mom-google-fonts');
        if (existing) existing.remove();
        const link = document.createElement('link');
        link.id = 'mom-google-fonts';
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?${googleFamilies.map((f) => `family=${f}`).join('&')}&display=swap`;
        document.head.appendChild(link);
    }

    // Load self-hosted fonts via @font-face
    const selfHosted = selected.filter((f) => f.selfHosted && f.files);
    if (selfHosted.length > 0) {
        const existing = document.getElementById('mom-self-hosted-fonts');
        if (existing) existing.remove();
        const fontFaces = selfHosted.flatMap((f) =>
            f.files.map((file) =>
                `@font-face { font-family: ${f.family.split(',')[0]}; src: url('${file.file}') format('woff2'); font-weight: ${file.weight}; font-style: normal; font-display: swap; }`
            )
        );
        const style = document.createElement('style');
        style.id = 'mom-self-hosted-fonts';
        style.textContent = fontFaces.join('\n');
        document.head.appendChild(style);
    }
}

// ── Color utility helpers ─────────────────────────────────
function hexToRgba(hex, alpha) {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

function blendHex(hex1, hex2, ratio) {
    const h1 = hex1.replace('#', ''), h2 = hex2.replace('#', '');
    const r = Math.round(parseInt(h1.substring(0, 2), 16) * (1 - ratio) + parseInt(h2.substring(0, 2), 16) * ratio);
    const g = Math.round(parseInt(h1.substring(2, 4), 16) * (1 - ratio) + parseInt(h2.substring(2, 4), 16) * ratio);
    const b = Math.round(parseInt(h1.substring(4, 6), 16) * (1 - ratio) + parseInt(h2.substring(4, 6), 16) * ratio);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// ── Higher-level convenience: apply theme + fonts straight from a
// siteConfig content blob. Used in two places:
//   1. `src/index.js` — called synchronously BEFORE React mounts, seeded
//      from `getCachedSiteContent()`. Eliminates the first-paint color
//      flash for returning visitors.
//   2. `src/App.js` — called inside a useEffect after the async siteConfig
//      fetch resolves, to apply any admin theme changes that landed since
//      the cached snapshot was written.
// Centralizing the extraction-and-application logic keeps the two call
// sites in lockstep — adding a new theme key only requires editing this
// one function.
function applyThemeFromSiteContent(content) {
    if (!content) return;
    const custom = {};
    if (content.themeCream) custom.cream = content.themeCream;
    if (content.themeCreamDark) custom.creamDark = content.themeCreamDark;
    if (content.themePrimary) custom.terracotta = content.themePrimary;
    if (content.themePrimaryDark) custom.terracottaDark = content.themePrimaryDark;
    if (content.themePrimaryLight) custom.terracottaLight = content.themePrimaryLight;
    if (content.themeCharcoal) custom.charcoal = content.themeCharcoal;
    if (content.themeWarmGray) custom.warmGray = content.themeWarmGray;
    if (content.themeSuccess) custom.success = content.themeSuccess;
    if (content.themeDanger) custom.danger = content.themeDanger;
    if (content.themeLink) custom.link = content.themeLink;
    if (content.themeLinkHover) custom.linkHover = content.themeLinkHover;
    if (content.themeCardBg) custom.cardBg = content.themeCardBg;
    if (content.themeNavCardBg) custom.navCardBg = content.themeNavCardBg;
    if (content.themeTileBg) custom.tileBg = content.themeTileBg;
    if (content.themeStat) custom.stat = content.themeStat;
    if (content.themeFilterPillBg) custom.filterPillBg = content.themeFilterPillBg;
    if (content.themeFilterPillText) custom.filterPillText = content.themeFilterPillText;
    if (content.themeFilterPillActiveBg) custom.filterPillActiveBg = content.themeFilterPillActiveBg;
    if (content.themeFilterPillActiveText) custom.filterPillActiveText = content.themeFilterPillActiveText;

    const fonts = {};
    if (content.themeHeadingFont) {
        const heading = HEADING_FONT_OPTIONS.find((f) => f.key === content.themeHeadingFont);
        if (heading) fonts.serif = heading.family;
    }
    if (content.themeBodyFont) {
        const body = BODY_FONT_OPTIONS.find((f) => f.key === content.themeBodyFont);
        if (body) fonts.sans = body.family;
    }

    if (content.themeHeadingFont || content.themeBodyFont) {
        loadGoogleFonts(content.themeHeadingFont, content.themeBodyFont);
    }

    applyThemeToDOM(
        Object.keys(custom).length > 0 ? custom : {},
        Object.keys(fonts).length > 0 ? fonts : null
    );
}

// Expose utilities for components
export { hexToRgba, applyThemeToDOM, applyThemeFromSiteContent, buildTheme, DEFAULT_COLORS, HEADING_FONT_OPTIONS, BODY_FONT_OPTIONS, loadGoogleFonts };

// ── Default export: theme built from defaults ─────────────
const theme = buildTheme(DEFAULT_COLORS);
export default theme;